import {useRef, useState} from "react"
import {toast} from "react-toastify"

import fetcher from "../fetcher"
import {useModalsStore} from "../stores/modalsStore"
import {TaxRegionModal, TAX_REGION_MODAL_ID} from "../components/TaxRegionModal"

/**
 * "Which tax region applies to this ZIP?" — asked at ZIP-entry time, before
 * anything is priced.
 *
 * Most ZIPs map to a single MarketScan tax region and nothing changes. A few
 * straddle several, and MarketScan silently applies the highest rate unless we
 * name one, so those need the user to pick before the quote is scanned.
 *
 * `resolveTaxRegion` returns a promise for the chosen RegionID, or null when no
 * explicit choice applies (one region, no regions, lookup failed, or the user
 * cancelled). null always means "carry on exactly as before" — AutoTaxRate is
 * left to do what it does today.
 */
export const useTaxRegionFlow = () => {
    const openModal = useModalsStore((state) => state.openModal)
    const [isLookingUp, setIsLookingUp] = useState(false)

    // The ZIP of the most recent request. Responses are matched against it
    // rather than assumed to arrive in order — nothing in the app cancels
    // in-flight requests, so a slow lookup for a ZIP the user has already moved
    // on from would otherwise open the modal with the wrong regions. The lookup
    // response echoes `zipcode` precisely so this comparison is possible.
    const latestZipcodeRef = useRef(null)

    const lookupTaxRegions = async (zipcode) => {
        latestZipcodeRef.current = zipcode
        setIsLookingUp(true)

        try {
            const {data} = await fetcher.getTaxRegions(zipcode)

            if (data?.zipcode !== latestZipcodeRef.current) return null   // superseded

            return data?.regions || []
        } catch (error) {
            // Degrade rather than block: a malformed/unknown ZIP or a MarketScan
            // blip must not stop the user from quoting. They get today's
            // behaviour (AutoTaxRate), which is what they'd get without this
            // feature at all.
            toast.warn('Could not check tax regions for this ZIP code. Continuing with the default tax rate.')
            return null
        } finally {
            setIsLookingUp(false)
        }
    }

    /**
     * @returns Promise<number|null> the selected RegionID, or null for "no
     *          explicit selection". Settles only once the user has decided.
     */
    const resolveTaxRegion = async (location, {isPurchase = false, isStale} = {}) => {
        const zipcode = location?.zipcode
        if (!zipcode) return null

        const regions = await lookupTaxRegions(zipcode)

        // The caller has moved on while this lookup ran: never prompt for it.
        if (isStale?.()) return null

        // A stale response resolves to null and is dropped by the caller's own
        // ordering; one region needs no choice (MarketScan's automatic pick is
        // that region), and zero regions leaves today's behaviour untouched.
        if (!regions || regions.length <= 1) return null

        return new Promise((resolve) => {
            openModal({
                element: TaxRegionModal,
                id: TAX_REGION_MODAL_ID,
                props: {
                    zipcode,
                    regions,
                    isPurchase,
                    onApply: (regionId) => resolve(regionId),
                    // Cancel keeps the location the user just picked and simply
                    // makes no explicit tax-region choice, so the quote prices
                    // exactly as it does today. Reverting the ZIP instead would
                    // silently undo a deliberate action.
                    onCancel: () => resolve(null),
                },
            })
        })
    }

    return {isLookingUp, resolveTaxRegion, lookupTaxRegions}
}
